import { gql } from '@apollo/client'

const ADMIN_PROVIDER_SUMMARY_FIELDS = gql`
  fragment AdminProviderSummaryFields on AdminProviderProfile {
    id
    verificationStatus
    onVacation
    completedJobs
    averageRating
    totalReviews
    createdAt
    user {
      id
      firstName
      lastName
      email
      createdAt
    }
  }
`

const ADMIN_BOOKING_SUMMARY_FIELDS = gql`
  fragment AdminBookingSummaryFields on AdminBooking {
    id
    status
    scheduledDate
    startTime
    endTime
    priceCents
    cancelledBy
    cancellationReason
    createdAt
    customer {
      id
      firstName
      lastName
      email
    }
    providerProfile {
      id
      verificationStatus
      user {
        id
        firstName
        lastName
        email
      }
    }
    serviceType {
      id
      name
      nameEl
      translationKey
    }
  }
`

export const ADMIN_STATS_QUERY = gql`
  query AdminStatsForPanel {
    adminStats {
      totalUsers
      totalProviders
      pendingVerifications
      totalBookings
      bookingsThisWeek
      bookingsThisMonth
      revenueThisMonthCents
      activeProviders
      averagePlatformRating
    }
  }
`

export const ADMIN_PROVIDERS_QUERY = gql`
  ${ADMIN_PROVIDER_SUMMARY_FIELDS}

  query AdminProvidersForPanel($status: String, $search: String, $page: Int, $perPage: Int) {
    adminProviders(status: $status, search: $search, page: $page, perPage: $perPage) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        ...AdminProviderSummaryFields
      }
    }
  }
`

export const ADMIN_PROVIDER_DETAIL_QUERY = gql`
  ${ADMIN_BOOKING_SUMMARY_FIELDS}

  query AdminProviderDetailForPanel($id: ID!) {
    adminProviderDetail(id: $id) {
      id
      bio
      yearsExperience
      languages
      hourlyRateCents
      verificationStatus
      verificationDocumentUrl
      onVacation
      completedJobs
      averageRating
      totalReviews
      rejectionReason
      suspensionReason
      createdAt
      updatedAt
      user {
        id
        firstName
        lastName
        fullName
        email
        phone
        avatarUrl
        role
        createdAt
      }
      providerServiceOfferings {
        id
        customPriceCents
        customDurationMinutes
        serviceType {
          id
          name
          nameEl
          translationKey
        }
      }
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
      bookings(limit: 10) {
        ...AdminBookingSummaryFields
      }
    }
  }
`

export const ADMIN_BOOKINGS_QUERY = gql`
  ${ADMIN_BOOKING_SUMMARY_FIELDS}

  query AdminBookingsForPanel(
    $status: String
    $dateFrom: ISO8601Date
    $dateTo: ISO8601Date
    $search: String
    $page: Int
    $perPage: Int
  ) {
    adminBookings(status: $status, dateFrom: $dateFrom, dateTo: $dateTo, search: $search, page: $page, perPage: $perPage) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        ...AdminBookingSummaryFields
      }
    }
  }
`

export const ADMIN_APPROVE_PROVIDER_MUTATION = gql`
  mutation AdminApproveProviderFromPanel($id: ID!) {
    adminApproveProvider(input: { id: $id }) {
      providerProfile {
        id
        verificationStatus
      }
      errors
    }
  }
`

export const ADMIN_REJECT_PROVIDER_MUTATION = gql`
  mutation AdminRejectProviderFromPanel($id: ID!, $reason: String!) {
    adminRejectProvider(input: { id: $id, reason: $reason }) {
      providerProfile {
        id
        verificationStatus
        rejectionReason
      }
      errors
    }
  }
`

export const ADMIN_SUSPEND_PROVIDER_MUTATION = gql`
  mutation AdminSuspendProviderFromPanel($id: ID!, $reason: String!) {
    adminSuspendProvider(input: { id: $id, reason: $reason }) {
      providerProfile {
        id
        verificationStatus
        suspensionReason
      }
      errors
    }
  }
`

export const ADMIN_UNSUSPEND_PROVIDER_MUTATION = gql`
  mutation AdminUnsuspendProviderFromPanel($id: ID!) {
    adminUnsuspendProvider(input: { id: $id }) {
      providerProfile {
        id
        verificationStatus
        suspensionReason
      }
      errors
    }
  }
`

export const ADMIN_CANCEL_BOOKING_MUTATION = gql`
  mutation AdminCancelBookingFromPanel($bookingId: ID!, $reason: String!) {
    adminCancelBooking(input: { bookingId: $bookingId, reason: $reason }) {
      booking {
        id
        status
        cancelledBy
        cancellationReason
      }
      errors
    }
  }
`

export interface AdminPageInfo {
  hasNextPage: boolean
  endCursor: string | null
}

export interface AdminUserSummary {
  id: string
  firstName: string
  lastName: string
  email: string
  createdAt: string
}

export interface AdminServiceTypeSummary {
  id: string
  name: string
  nameEl: string
  translationKey: string
}

export interface AdminProviderSummary {
  id: string
  verificationStatus: string
  onVacation: boolean
  completedJobs: number
  averageRating: number
  totalReviews: number
  createdAt: string
  user: AdminUserSummary
}

export interface AdminProviderDetail extends AdminProviderSummary {
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  verificationDocumentUrl: string | null
  rejectionReason: string | null
  suspensionReason: string | null
  updatedAt: string
  user: AdminUserSummary & {
    fullName: string
    phone: string | null
    avatarUrl: string | null
    role: 'customer' | 'provider'
  }
  providerServiceOfferings: Array<{
    id: string
    customPriceCents: number | null
    customDurationMinutes: number | null
    serviceType: AdminServiceTypeSummary
  }>
  serviceAreas: Array<{
    id: string
    postalCode: string
    areaName: string | null
    city: string
  }>
  bookings: AdminBookingSummary[]
}

export interface AdminBookingSummary {
  id: string
  status: string
  scheduledDate: string
  startTime: string
  endTime: string
  priceCents: number
  cancelledBy: string | null
  cancellationReason: string | null
  createdAt: string
  customer: AdminUserSummary
  providerProfile: {
    id: string
    verificationStatus: string
    user: AdminUserSummary
  }
  serviceType: AdminServiceTypeSummary
}

export interface AdminStatsData {
  adminStats: {
    totalUsers: number
    totalProviders: number
    pendingVerifications: number
    totalBookings: number
    bookingsThisWeek: number
    bookingsThisMonth: number
    revenueThisMonthCents: number
    activeProviders: number
    averagePlatformRating: number
  }
}

export interface AdminProvidersData {
  adminProviders: {
    totalCount: number
    pageInfo: AdminPageInfo
    nodes: AdminProviderSummary[]
  }
}

export interface AdminProvidersVariables {
  status?: string
  search?: string
  page?: number
  perPage?: number
}

export interface AdminProviderDetailData {
  adminProviderDetail: AdminProviderDetail | null
}

export interface AdminProviderDetailVariables {
  id: string
}

export interface AdminBookingsData {
  adminBookings: {
    totalCount: number
    pageInfo: AdminPageInfo
    nodes: AdminBookingSummary[]
  }
}

export interface AdminBookingsVariables {
  status?: string
  dateFrom?: string
  dateTo?: string
  search?: string
  page?: number
  perPage?: number
}

export interface AdminApproveProviderData {
  adminApproveProvider: {
    providerProfile: {
      id: string
      verificationStatus: string
    } | null
    errors: string[]
  }
}

export interface AdminApproveProviderVariables {
  id: string
}

export interface AdminRejectProviderData {
  adminRejectProvider: {
    providerProfile: {
      id: string
      verificationStatus: string
      rejectionReason: string | null
    } | null
    errors: string[]
  }
}

export interface AdminRejectProviderVariables {
  id: string
  reason: string
}

export interface AdminSuspendProviderData {
  adminSuspendProvider: {
    providerProfile: {
      id: string
      verificationStatus: string
      suspensionReason: string | null
    } | null
    errors: string[]
  }
}

export interface AdminSuspendProviderVariables {
  id: string
  reason: string
}

export interface AdminUnsuspendProviderData {
  adminUnsuspendProvider: {
    providerProfile: {
      id: string
      verificationStatus: string
      suspensionReason: string | null
    } | null
    errors: string[]
  }
}

export interface AdminUnsuspendProviderVariables {
  id: string
}

export interface AdminCancelBookingData {
  adminCancelBooking: {
    booking: {
      id: string
      status: string
      cancelledBy: string | null
      cancellationReason: string | null
    } | null
    errors: string[]
  }
}

export interface AdminCancelBookingVariables {
  bookingId: string
  reason: string
}
